/** @jsxImportSource ../i18n */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, RefreshCw, Settings2, Users } from 'lucide-react';
import api from '../services/api';
import { reportDate, reportTime } from '../components/reportShared';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { MascotAvatar } from '../components/mascotCatalog';
import { cx } from '../theme/cx';
import { Badge, Banner, Button, Card, PageHeader, Skeleton, SkeletonText, Spinner } from '../components/ui';

const validId = value => /^[1-9][0-9]*$/.test(String(value)) && Number.isSafeInteger(Number(value));

const RUN_STATUS = {
  completed: ['Terminé', 'gain'],
  failed: ['Échec du panel', 'warn'],
  running: ['En cours ou interrompu', 'neutral'],
};

const VERDICTS = {
  approve: ['Approuvé', 'gain'],
  reservations: ['Réserves', 'warn'],
  unknown: ['Avis non conclusif', 'neutral'],
  unavailable: ['Relecture indisponible', 'loss'],
};

const Tag = ({ entry: [label, tone] }) => <Badge tone={tone}>{label}</Badge>;

const runStatus = (status) => RUN_STATUS[status] || [status, 'neutral'];

// One collapsible item of a phase: the expert's text, or why it is missing.
// The summary never holds a heading: the markdown body owns the h2/h4 levels.
const EntryItem = ({ title, meta, badge, entry, open = false }) => (
  <details open={open} className="group rounded-control border border-line bg-surface-2/40 transition-colors open:bg-surface-2/70" data-testid={`entry-${entry.phase}`}>
    <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 [&::-webkit-details-marker]:hidden">
      <ChevronRight size={15} aria-hidden="true" className="flex-shrink-0 text-fg-subtle transition-transform duration-200 group-open:rotate-90 group-open:text-gold-400" />
      <span className="flex items-center gap-1.5">
        <MascotAvatar variant={entry.mascot_variant} name={entry.expert_name} size="sm" />
        {entry.phase === 'debate' && <><span className="text-fg-faint" aria-hidden="true">→</span><MascotAvatar variant={entry.counterpart_mascot} name={entry.counterpart_name} size="sm" /></>}
      </span>
      <span className="font-medium text-fg">{title}</span>
      {meta && <span className="text-xs text-fg-subtle">{meta}</span>}
      {(badge || entry.error) && (
        <span className="flex flex-wrap items-center gap-1.5">
          {badge}
          {entry.error && <Tag entry={['Indisponible', 'loss']} />}
        </span>
      )}
    </summary>
    <div className="border-t border-line px-4 pb-5 pt-4 sm:px-5">
      {entry.error
        ? <p className="break-words text-sm text-loss-300">{entry.error}</p>
        : entry.content.trim() ? <MarkdownProse variant="report" skipHtml>{entry.content}</MarkdownProse> : <p className="text-sm text-fg-muted">Contenu vide.</p>}
    </div>
  </details>
);

// A step of the vertical timeline: numbered gold pill for the main phases,
// a small marker for the sub-steps of the round table.
const Phase = ({ step, eyebrow, title, hint, children, empty, last = false }) => (
  <section className="relative pl-12 sm:pl-14">
    {!last && <span aria-hidden="true" className="absolute bottom-[-1.5rem] left-[15px] top-10 w-px bg-line sm:left-[19px]" />}
    <span
      aria-hidden="true"
      className={cx(
        'absolute left-0 top-0 inline-flex items-center justify-center rounded-full',
        step
          ? 'num h-8 w-8 border border-gold-500/40 bg-gold-900/70 text-sm font-semibold text-gold-200 sm:h-10 sm:w-10'
          : 'ml-[11px] mt-3 h-2.5 w-2.5 bg-gold-500/60 ring-4 ring-canvas sm:ml-[15px]',
      )}
    >
      {step}
    </span>
    <Card className="space-y-4">
      <div>
        {eyebrow && <p className="eyebrow mb-1 text-gold-400/90">{eyebrow}</p>}
        <h3 className="font-display text-xl font-normal text-fg">{title}</h3>
        {hint && <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{hint}</p>}
      </div>
      {React.Children.toArray(children).length ? <div className="space-y-3">{children}</div> : <p className="text-sm text-fg-muted">{empty}</p>}
    </Card>
  </section>
);

const agendaCounts = (entry) => {
  const items = Array.isArray(entry?.data) ? entry.data : [];
  const count = status => items.filter(it => it.status === status).length;
  return { consensus: count('consensus'), majority: count('majority'), open: count('open'), converged: count('converged'), persistent: count('persistent') };
};

const majorityBadge = (n) => (n > 0 ? <Tag entry={[`${n} à la majorité`, 'gold']} /> : null);

// Phase 3: convergence matrix and agenda, the round table turn by turn, then
// the outcome of the arbitrations.
const RoundTable = ({ entries }) => {
  const agenda = entries.find(e => e.phase === 'agenda');
  const outcome = entries.find(e => e.phase === 'outcome');
  const turns = entries.filter(e => e.phase === 'roundtable');
  const rounds = [...new Set(turns.map(t => t.round))].sort((a, b) => a - b);
  const start = agendaCounts(agenda);
  const end = agendaCounts(outcome);
  return (
    <>
      <Phase step="3" eyebrow="Phase 3" title="Matrice de convergence et ordre du jour" hint="Les décisions de chaque expert sont comparées ligne par ligne : les accords unanimes sont actés d'office, une nette majorité tranche sans débat, les points de friction sont soumis à la table ronde." empty="Aucun ordre du jour : le panel s'est arrêté avant cette phase.">
        {agenda && (
          <EntryItem entry={agenda} open
            title={agenda.expert_name === 'Panel' ? 'Ordre du jour établi par le panel' : `Ordre du jour — modéré par ${agenda.expert_name}`}
            badge={<><Tag entry={[`${start.consensus} acté(s) d'office`, 'gain']} />{majorityBadge(start.majority)}<Tag entry={[`${start.open} en arbitrage`, 'warn']} /></>} />
        )}
      </Phase>
      {agenda && (
        <Phase eyebrow="Phase 3 · débat" title="Table ronde" hint="Les experts interviennent à tour de rôle sur les seuls points ouverts ; chacun fait évoluer ou maintient sa position en la motivant." empty="Aucune table ronde : tous les points étaient actés d'office.">
          {rounds.map(r => (
            <div key={r} className="space-y-2" data-testid={`round-${r}`}>
              <h4 className="eyebrow pt-1 text-fg-muted">Tour {r}</h4>
              {turns.filter(t => t.round === r).map(t => {
                const n = t.data && typeof t.data === 'object' ? Object.keys(t.data).length : 0;
                return <EntryItem key={t.id} entry={t} title={t.expert_name} meta={`${t.model} · ${n} position${n > 1 ? 's' : ''} déclarée${n > 1 ? 's' : ''}`} />;
              })}
            </div>
          ))}
        </Phase>
      )}
      {outcome && (
        <Phase eyebrow="Phase 3 · issue" title="Issue des arbitrages" empty="">
          <EntryItem entry={outcome} open title="Consensus, convergences et avis contradictoires"
            badge={<><Tag entry={[`${end.converged} convergence(s)`, 'gain']} />{majorityBadge(end.majority)}<Tag entry={[`${end.persistent} désaccord(s) persistant(s)`, 'warn']} /></>} />
        </Phase>
      )}
    </>
  );
};

const Meta = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-1.5 break-words text-sm text-fg-body">{children}</dd>
  </div>
);

const RunDetail = ({ run }) => {
  const byPhase = phase => run.entries.filter(e => e.phase === phase);
  return (
    <div className="min-w-0 space-y-6">
      <Card as="header" padding="lg">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h2 className="font-display text-display-md font-normal text-fg">
            {run.kind === 'topic' ? `Débat sur ${run.topic_symbol} du ${reportDate(run.report_date)}` : run.kind === 'pilot' ? `Débat du pilote du ${reportDate(run.report_date)}` : `Panel du ${reportDate(run.report_date)}`}
          </h2>
          <Tag entry={runStatus(run.status)} />
        </div>
        <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          <Meta label="Experts ayant analysé"><span className="num">{run.expert_count}</span></Meta>
          <Meta label="Rapporteur">{run.rapporteur_name || '—'}</Meta>
          <Meta label="Tours de table ronde (max)"><span className="num">{run.debate_rounds}</span></Meta>
          <Meta label="Démarré le (heure de Paris)">{reportTime(run.created_at)}</Meta>
          <Meta label="Terminé le (heure de Paris)">{run.completed_at ? reportTime(run.completed_at) : '—'}</Meta>
          {run.kind === 'topic' && <Meta label="Question posée">{run.topic_question || 'Aucune : que faire de cette valeur ?'}</Meta>}
        </dl>
        {run.error && (
          <Banner tone="warn" role="alert" className="mt-5">
            {run.error}.{run.kind === 'topic' || run.kind === 'pilot' ? '' : ' Le rapport standard a été utilisé si une clé IA générale est configurée.'}
          </Banner>
        )}
        {run.daily_report_id && (
          <Button as={Link} to={`/reports/${run.daily_report_id}`} variant="secondary" trailingIcon={ArrowRight} className="mt-5">
            Lire le rapport global consolidé
          </Button>
        )}
      </Card>

      <div className="space-y-6">
        <Phase step="2" eyebrow="Phase 2" title="Analyses individuelles" hint="Chaque expert analyse le même dossier de recherche, sans voir les autres." empty="Aucune analyse enregistrée.">
          {byPhase('analysis').map(e => <EntryItem key={e.id} entry={e} title={e.expert_name} meta={`${e.provider} · ${e.model}`} />)}
        </Phase>

        {byPhase('debate').length > 0 ? (
          <Phase step="3" eyebrow="Phase 3" title="Débats" hint="Chaque expert critique l'analyse de chaque autre expert, qui lui répond." empty="">
            {byPhase('debate').map(e => <EntryItem key={e.id} entry={e} title={`${e.expert_name} → ${e.counterpart_name}`} meta={`${e.expert_name} critique, ${e.counterpart_name} répond`} />)}
          </Phase>
        ) : (
          <RoundTable entries={run.entries} />
        )}

        <Phase step="4" eyebrow="Phase 4" title="Consolidation" last hint="Le rapporteur rédige un projet commun ; chaque autre expert l'approuve ou émet des réserves, intégrées ou listées comme divergences dans le rapport final." empty="Aucune consolidation : le panel s'est arrêté avant cette phase.">
          {byPhase('draft').map(e => <EntryItem key={e.id} entry={e} title={`Projet du rapporteur — ${e.expert_name}`} meta={e.model} />)}
          {byPhase('review').map(e => <EntryItem key={e.id} entry={e} title={`Relecture — ${e.expert_name}`} meta={e.model} badge={e.verdict && <Tag entry={VERDICTS[e.verdict] || VERDICTS.unknown} />} />)}
        </Phase>
      </div>
    </div>
  );
};

const PanelStatus = ({ panel, error }) => {
  if (error) return <p role="alert" className="text-loss-300">Impossible de charger la configuration des experts.</p>;
  if (!panel) return <Spinner label="Chargement…" />;
  const enabled = panel.experts.filter(e => e.enabled).length;
  const ready = enabled >= panel.min_experts;
  return (
    <>
      <p className="font-medium text-fg">
        {ready
          ? `Panel prêt : ${enabled} experts actifs.`
          : `Panel incomplet : ${enabled} expert${enabled > 1 ? 's' : ''} actif${enabled > 1 ? 's' : ''} sur ${panel.min_experts} requis.`}
      </p>
      <p className="mt-1.5 text-fg-muted">
        {ready
          ? "Les analyses et débats apparaissent ici après chaque génération du rapport quotidien."
          : "Tant que le panel est incomplet, le rapport quotidien actuel continue d'être généré et envoyé."}
      </p>
      <Button as={Link} to="/settings?tab=experts" variant="link" leadingIcon={Settings2} className="mt-3">Configurer les experts</Button>
    </>
  );
};

// Archive of the expert panel runs. Reading or refreshing never triggers an analysis.
const ExpertReports = () => {
  const { runId } = useParams();
  const [panel, setPanel] = useState(null);
  const [panelError, setPanelError] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState('');
  const [run, setRun] = useState(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState('');
  const [version, setVersion] = useState(0);
  const invalidId = Boolean(runId && !validId(runId));
  const selectedId = runId ? (invalidId ? null : runId) : runs[0] ? String(runs[0].id) : null;
  const current = run && String(run.id) === selectedId ? run : null;

  useEffect(() => {
    let active = true;
    setRunsLoading(true);
    setRunsError('');
    api.get('/experts')
      .then(res => { if (active) { setPanel(res.data); setPanelError(false); } })
      .catch(() => { if (active) setPanelError(true); });
    api.get('/analysis/expert-reports', { params: { limit: 30 } })
      .then(res => {
        if (!Array.isArray(res.data)) throw new Error('invalid list');
        if (active) setRuns(res.data);
      })
      .catch(() => { if (active) setRunsError("Impossible de charger l'historique des panels."); })
      .finally(() => { if (active) setRunsLoading(false); });
    return () => { active = false; };
  }, [version]);

  useEffect(() => {
    setRun(null);
    setRunError('');
    if (!selectedId) return undefined;
    let active = true;
    setRunLoading(true);
    api.get(`/analysis/expert-reports/${encodeURIComponent(selectedId)}`)
      .then(res => {
        if (String(res.data?.id) !== selectedId || !Array.isArray(res.data?.entries)) throw new Error('invalid detail');
        if (active) setRun(res.data);
      })
      .catch(err => { if (active) setRunError(err.response?.status === 404 ? 'Panel introuvable ou non accessible.' : 'Impossible de charger ce panel.'); })
      .finally(() => { if (active) setRunLoading(false); });
    return () => { active = false; };
  }, [selectedId, version]);

  const busy = runsLoading || runLoading;
  const showStatus = !runsLoading && (runs.length === 0 || (panel && panel.experts.filter(e => e.enabled).length < panel.min_experts));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Rapports d'experts"
        description={(
          <>
            <p>Analyses individuelles de chaque expert et débats entre experts, avant la consolidation en rapport global.</p>
            <p className="mt-1 text-sm text-fg-subtle">La consultation et l'actualisation ne déclenchent aucune analyse ni recherche Tavily.</p>
          </>
        )}
        actions={(
          <Button variant="secondary" leadingIcon={RefreshCw} loading={busy} onClick={() => setVersion(v => v + 1)} aria-label="Actualiser les rapports d'experts">
            Actualiser
          </Button>
        )}
      />

      {showStatus && (
        <Card tone="gold" className="flex items-start gap-4 text-sm" data-testid="expert-panel-status">
          <span aria-hidden="true" className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-control border border-line bg-surface-2 text-gold-400 sm:inline-flex"><Users size={18} /></span>
          <div className="min-w-0"><PanelStatus panel={panel} error={panelError} /></div>
        </Card>
      )}

      {(runs.length > 0 || runsError || runId) && (
        <div className="grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
          <Card as="aside" className="self-start" aria-label="Historique des panels">
            <h2 className="mb-4 text-[17px] font-semibold text-fg">Historique</h2>
            {runsError && (
              <Banner tone="loss" role="alert" compact className="mb-4" action={<Button variant="link" onClick={() => setVersion(v => v + 1)}>Réessayer</Button>}>
                {runsError}
              </Banner>
            )}
            <nav className="max-h-80 space-y-2 overflow-y-auto pr-1 xl:max-h-[70vh]" aria-label="Choisir un panel">
              {runs.map(item => {
                const active = String(item.id) === selectedId;
                return (
                  <Link
                    key={item.id}
                    to={`/reports/experts/${item.id}`}
                    aria-current={active ? 'page' : undefined}
                    className={cx(
                      'relative block rounded-control border p-3 transition-colors duration-150',
                      active ? 'border-gold-500/40 bg-gold-950/40 before:absolute before:inset-y-3 before:left-0 before:w-0.5 before:rounded-full before:bg-gold-500' : 'border-line hover:border-line-strong hover:bg-surface-2/60',
                    )}
                  >
                    <p className="font-display text-base text-fg">{reportDate(item.report_date)}</p>
                    {item.kind === 'topic' && <p className="mt-0.5 text-xs font-medium text-gold-300">Débat à la demande · {item.topic_symbol}</p>}
                    {item.kind === 'pilot' && <p className="mt-0.5 text-xs font-medium text-info-300">Débat du pilote</p>}
                    <p className="mt-1 text-xs text-fg-subtle">{item.expert_count} expert{item.expert_count > 1 ? 's' : ''}{item.rapporteur_name ? ` · rapporteur ${item.rapporteur_name}` : ''}</p>
                    <div className="mt-2.5"><Tag entry={runStatus(item.status)} /></div>
                  </Link>
                );
              })}
            </nav>
          </Card>
          <section className="min-w-0" aria-label="Détail du panel" aria-busy={runLoading}>
            {invalidId ? (
              <Card>
                <Banner tone="loss" role="alert">Identifiant de panel invalide.</Banner>
                <Button as={Link} to="/reports/experts" variant="link" className="mt-4">Retour aux rapports d'experts</Button>
              </Card>
            ) : runLoading ? (
              <Card padding="lg" role="status">
                <span className="sr-only">Chargement du panel…</span>
                <div aria-hidden="true">
                  <Skeleton className="h-7 w-1/2" />
                  <SkeletonText lines={4} className="mt-6" />
                </div>
              </Card>
            ) : runError ? (
              <Card>
                <Banner tone="loss" role="alert">{runError}</Banner>
                <Button variant="secondary" leadingIcon={RefreshCw} className="mt-4" onClick={() => setVersion(v => v + 1)}>Réessayer</Button>
              </Card>
            ) : current ? (
              <RunDetail run={current} />
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
};

export default ExpertReports;
